import { inject, Injectable } from '@angular/core';

import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ICustomer } from '../../../../models/customer.model';
import { StatisticsService } from '../../../../services/statistics-service';
import { BaryChart } from '../../../../models/bary-chart.model';
import { StatsPeriod } from '../../../../models/stats-period.model';
import { MdbBaryChartModel } from '../../../../models/mdb-bary-chart.model';
import { StatsButton } from '../../../../models/stats-button';
import Chart from 'chart.js';

type Dataset = { data: Array<string | number | Date>; labels: Array<string> };

@Injectable({
  providedIn: 'root',
})
export class ChartsStatsService {
  readonly #statisticsService = inject(StatisticsService);
  readonly #translateService = inject(TranslateService);

  private GREEN_COLOR: string = 'rgba(23,117,23,0.5)';
  private DARK_GRAY_1: string = '#3c3c3c';
  private DARK_GRAY_2: string = 'rgba(88,88,88,0.5)';
  private LIGHT_GRAY_1: string = 'rgba(163,163,163,0.5)';
  private LIGHT_GRAY_2: string = 'rgba(126,126,126,0.5)';

  private LABEL_CARBON_FOOTPRINT: string = this.#translateService.instant('LABEL.CARBON_FOOTPRINT');
  private LABEL_FUEL_TAX: string = this.#translateService.instant('LABEL.FUEL_TAX');
  private LABEL_INSURANCE_MARGIN: string = this.#translateService.instant('LABEL.INSURANCE_MARGIN');
  private LABEL_OPTIONS: string = this.#translateService.instant('LABEL.OPTIONS');
  private LABEL_TRANSPORT_PRICE: string = this.#translateService.instant('LABEL.TRANSPORT_PRICE');

  public chartType = 'bar';
  public chartDatasets!: Array<any>;
  public chartLabels!: Array<any>;
  public chartColors: Array<any> = [
    {
      backgroundColor: Array.from(Array(44), () => this.GREEN_COLOR),
      borderColor: Array.from(Array(44), () => this.DARK_GRAY_1),
      borderWidth: 1,
    },
    {
      backgroundColor: Array.from(Array(44), () => this.LIGHT_GRAY_1),
      borderColor: Array.from(Array(44), () => this.DARK_GRAY_1),
      borderWidth: 1,
    },

    {
      backgroundColor: Array.from(Array(44), () => this.LIGHT_GRAY_2),
      borderColor: Array.from(Array(44), () => this.DARK_GRAY_1),
      borderWidth: 1,
    },
    {
      backgroundColor: Array.from(Array(44), () => this.DARK_GRAY_2),
      borderColor: Array.from(Array(44), () => this.DARK_GRAY_1),
      borderWidth: 1,
    },
  ];
  public chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2.5,
    scales: {
      xAxes: [{ stacked: true }],
      yAxes: [
        {
          display: true,
          stacked: true,
          ticks: {
            beginAtZero: true, // minimum value will be 0
          },
        },
      ],
    },
    tooltips: {
      mode: 'x',
      callbacks: {
        LABEL_CARBON_FOOTPRINT: this.LABEL_CARBON_FOOTPRINT,
        LABEL_FUEL_TAX: this.LABEL_FUEL_TAX,
        LABEL_INSURANCE_MARGIN: this.LABEL_INSURANCE_MARGIN,
        LABEL_OPTIONS: this.LABEL_OPTIONS,
        LABEL_TRANSPORT_PRICE: this.LABEL_TRANSPORT_PRICE,
        afterTitle(item: Chart.ChartTooltipItem[], data: Chart.ChartData): string | string[] {
          if (item.length > 1) {
            const sum = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
              item.reduce((_sum, it) => {
                if (it.yLabel) {
                  _sum += parseFloat(it.yLabel.toString());
                }
                return _sum;
              }, 0)
            );
            return 'Total : ' + sum;
          }
          return '';
        },
        label(tooltipItem: any, data: any): string {
          let label: string = data.datasets[tooltipItem.datasetIndex].label || '';
          if (label) {
            let options: { style: string; currency?: string; unit?: string } = { style: 'decimal' };
            if ([this.LABEL_OPTIONS, this.LABEL_TRANSPORT_PRICE, this.LABEL_FUEL_TAX, this.LABEL_INSURANCE_MARGIN].includes(label)) {
              options = { style: 'currency', currency: 'EUR' };
            } else if (label === this.LABEL_CARBON_FOOTPRINT) {
              options = { style: 'unit', unit: 'kilogram' };
            }
            label += ' : ';
            label += new Intl.NumberFormat('fr-FR', options).format(tooltipItem.value);
          }
          return label;
        },
      },
    },
  };
  public chartLegend = false;
  public legend: string | undefined;

  private static calculateFromDataset(chartDatasets: any[]): number {
    let sum = 0;
    for (let i = 0; i < chartDatasets.length; i++) {
      for (const data of chartDatasets[i]['data']) {
        sum = sum + Number(data);
      }
    }
    return sum;
  }

  private setBarChart(chart: BaryChart[], query: string, period: StatsPeriod, billDate: boolean, maskOptions: boolean): void {
    const chartModel = this.extractData(chart, period);
    this.chartDatasets = this.formatDataset(chartModel.dataset, query, billDate, maskOptions);
    this.chartLabels = chartModel.labels;
    this.legend = this.#translateService.instant('CHART.TITLE.BILLED_PRE_TAXES_REVENUES');
  }

  public getStats(customer: ICustomer, query: string, table: string, period: StatsPeriod, id: number, billDate: boolean = false, maskOptions: boolean = false): void {
    this.#statisticsService.getChartDtoStatsPeriod(customer, query, table, period, id, billDate).subscribe((httpResponse: HttpResponse<BaryChart[] | null>) => {
      if (httpResponse.body) {
        this.setBarChart(httpResponse.body, query, period, billDate, maskOptions);
      }
    });
  }

  public getPersoStats(
    customer: ICustomer,
    query: string,
    table: string,
    startDate: Date,
    endDate: Date,
    id: number,
    billDate: boolean = false,
    maskOptions: boolean = false
  ): void {
    this.#statisticsService.getChartDtoStats(customer, query, table, startDate, endDate, id, billDate).subscribe((httpResponse: HttpResponse<BaryChart[]>) => {
      if (httpResponse.body) {
        this.setBarChart(httpResponse.body, query, StatsPeriod.Perso, billDate, maskOptions);
      }
    });
  }

  private extractData(res: BaryChart | BaryChart[], period: StatsPeriod): MdbBaryChartModel {
    let dataSet: Array<any> = [];
    const body = res;
    if (body instanceof Array) {
      dataSet = body.map(item => this.getBaryChartLine(item, period));
    } else {
      const line = this.getBaryChartLine(body, period);
      dataSet.push(line);
    }
    return new MdbBaryChartModel(dataSet, dataSet[0].labels);
  }

  private getBaryChartLine(bodyLine: BaryChart, period: StatsPeriod): Dataset {
    const result: Dataset = { data: [], labels: [] };
    for (const entry of bodyLine.data) {
      result.data.push(Number(entry[1]).toFixed(2));
      if (period === 'CurrentYear') {
        result.labels.push(this.#translateService.instant('CHART.AXE_X.MONTH.' + entry[0]));
      } else {
        result.labels.push('' + entry[0]);
      }
    }
    return result;
  }

  private formatDataset(
    dataset: Array<Dataset>,
    query: string,
    billDate: boolean,
    maskOptions: boolean
  ): Array<{
    data: Array<string | number | Date>;
    label: string;
  }> {
    const labels: Array<string> = [];
    if (query === 'turnoverBilled') {
      labels.push(this.LABEL_TRANSPORT_PRICE);
      if (!maskOptions) {
        labels.push(this.LABEL_OPTIONS);
      }
      labels.push(this.LABEL_INSURANCE_MARGIN);
      if (billDate) {
        labels.push(this.LABEL_FUEL_TAX);
      }
    } else if (query === 'transport') {
      labels.push(this.#translateService.instant('LABEL.TRANSPORTS'));
    } else if (query === 'carbonfootprint') {
      labels.push(this.LABEL_CARBON_FOOTPRINT);
    } else {
      labels.push(this.#translateService.instant('LABEL.' + query.toUpperCase()));
    }
    return dataset.map((arr: Dataset, i: number) => ({
      data: arr.data,
      label: labels[i],
    }));
  }

  public calculateTotal(
    customer: ICustomer,
    statsButton: StatsButton,
    table: string,
    period: StatsPeriod,
    id: number,
    startDate: Date | undefined,
    endDate: Date | undefined,
    billDate: boolean = false
  ): void {
    if (period === StatsPeriod.Perso && startDate && endDate) {
      this.getPersoChartsStats(customer, statsButton, table, startDate, endDate, id, billDate);
    } else {
      this.getChartsStats(customer, statsButton, table, period, id, billDate);
    }
  }

  public refreshButtons(
    customer: ICustomer,
    buttons: StatsButton[],
    table: string,
    period: StatsPeriod,
    id: number,
    startDate: Date | undefined,
    endDate: Date | undefined,
    billDate: boolean = false
  ): void {
    for (const statsButton of buttons) {
      this.calculateTotal(customer, statsButton, table, period, id, startDate, endDate, billDate);
    }
  }

  private getChartsStats(customer: ICustomer, statsButton: StatsButton, table: string, period: StatsPeriod, id: number, billDate: boolean = false): void {
    this.#statisticsService.getChartDtoStatsPeriod(customer, statsButton.query, table, period, id, billDate).subscribe((httpResponse: HttpResponse<BaryChart[]>) => {
      if (httpResponse.body) {
        const chartModel = this.extractData(httpResponse.body, period);
        statsButton.value = ChartsStatsService.calculateFromDataset(chartModel.dataset);
      }
    });
  }

  private getPersoChartsStats(customer: ICustomer, statsButton: StatsButton, table: string, startDate: Date, endDate: Date, id: number, billDate: boolean = false): void {
    this.#statisticsService.getChartDtoStats(customer, statsButton.query, table, startDate, endDate, id, billDate).subscribe((httpResponse: HttpResponse<BaryChart[]>) => {
      if (httpResponse.body) {
        const chartModel = this.extractData(httpResponse.body, StatsPeriod.Perso);
        statsButton.value = ChartsStatsService.calculateFromDataset(chartModel.dataset);
      }
    });
  }
}
